import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Check, ChevronDown, ChevronUp, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContentWithoutClose, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";

interface PricingModalProps {
  onClose: () => void;
  selectedTier: string | null;
}

interface LicenseTier {
  name: string;
  basePrice: number;
  description: string;
  features: string[];
}

interface BundleOption {
  count: number;
  basicPrice: number;
  professionalPrice: number;
  enterprisePrice: number;
  basicSavings: number;
  professionalSavings: number;
  enterpriseSavings: number;
}

interface AddOn {
  id: string;
  name: string;
  price: number;
  description: string;
  compatibleTiers: string[];
}

const tiers: LicenseTier[] = [
  {
    name: "Basic Creator License",
    basePrice: 49,
    description: "Perfect for small creators and YouTubers up to 100K subscribers",
    features: [
      "YouTube videos (gaming, vlogs, tutorials)",
      "Instagram/TikTok content",
      "Personal podcast intro/outro",
      "Student films",
      "Personal website background music",
      "Non-monetized content",
      "Single platform use only",
      "12-month license duration"
    ]
  },
  {
    name: "Professional License",
    basePrice: 199,
    description: "For professional content creators up to 1M subscribers",
    features: [
      "All Basic License features",
      "Monetized content across platforms",
      "Online courses and educational content",
      "Small business advertisements",
      "Local TV/Radio commercials",
      "Mobile apps and games",
      "Corporate presentations",
      "Wedding/Event videos",
      "Perpetual license"
    ]
  },
  {
    name: "Enterprise License",
    basePrice: 499,
    description: "For businesses and large channels with unlimited audience",
    features: [
      "All Professional License features",
      "National/International TV commercials",
      "Major brand campaigns",
      "Broadcast network shows",
      "Feature films",
      "Major video game releases",
      "National radio campaigns",
      "Multi-territory distribution",
      "White-label usage",
      "Perpetual license"
    ]
  }
];

const bundleOptions: BundleOption[] = [
  {
    count: 5,
    basicPrice: 199,
    professionalPrice: 799,
    enterprisePrice: 1999,
    basicSavings: 46,
    professionalSavings: 196,
    enterpriseSavings: 496,
  },
  {
    count: 10,
    basicPrice: 349,
    professionalPrice: 1499,
    enterprisePrice: 3999,
    basicSavings: 141,
    professionalSavings: 491,
    enterpriseSavings: 991,
  },
];

const addOns: AddOn[] = [
  {
    id: "extended-territory",
    name: "Extended Territory Rights",
    price: 149,
    description: "Expand your license to cover additional territories (Basic: +$299)",
    compatibleTiers: ["professional", "enterprise"],
  },
  {
    id: "broadcast-rights",
    name: "Broadcast Rights",
    price: 299,
    description: "Add TV, radio, and streaming platform rights",
    compatibleTiers: ["professional", "enterprise"],
  },
  {
    id: "custom-edit",
    name: "Custom Edit Service",
    price: 149,
    description: "Get your track professionally edited to custom specifications",
    compatibleTiers: ["basic", "professional", "enterprise"],
  },
];

export const PricingModal = ({ onClose, selectedTier }: PricingModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isOpen, setIsOpen] = useState(true);
  const [selectedLicenseTier, setSelectedLicenseTier] = useState<string>(selectedTier || "professional");
  const [quantity, setQuantity] = useState(1);
  const [isBundle, setIsBundle] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState<number | null>(null);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [trackTitle, setTrackTitle] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  
  // Form state for custom requests
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    requirements: ""
  });

  const [showLicenseDetails, setShowLicenseDetails] = useState<Record<string, boolean>>({
    basic: false,
    professional: false,
    enterprise: false,
  });
  const { toast } = useToast();

  useEffect(() => {
    calculateTotalPrice();
  }, [selectedLicenseTier, quantity, isBundle, selectedBundle, selectedAddOns]);

  const calculateTotalPrice = () => {
    if (selectedLicenseTier === "custom") {
      setTotalPrice(0);
      return;
    }

    let price = 0;
    const tier = tiers.find(t => t.name === selectedLicenseTier);

    if (isBundle && selectedBundle !== null) {
      const bundleOption = bundleOptions[selectedBundle];
      if (selectedLicenseTier === "basic") {
        price = bundleOption.basicPrice;
      } else if (selectedLicenseTier === "professional") {
        price = bundleOption.professionalPrice;
      } else if (selectedLicenseTier === "enterprise") {
        price = bundleOption.enterprisePrice;
      }
    } else {
      price = tier?.basePrice * quantity;
    }

    // Add the add-ons
    selectedAddOns.forEach((addOnId) => {
      const addOn = addOns.find((a) => a.id === addOnId);
      if (addOn) {
        // Handle special case for extended territory on basic tier
        if (addOnId === "extended-territory" && selectedLicenseTier === "basic") {
          price += 299;
        } else {
          price += addOn.price;
        }
      }
    });

    setTotalPrice(price);
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  const handleQuantityChange = (value: string) => {
    const newQuantity = parseInt(value);
    setQuantity(newQuantity);
    if (newQuantity > 1) {
      setIsBundle(true);
    } else {
      setIsBundle(false);
      setSelectedBundle(null);
    }
  };

  const handleBundleToggle = (checked: boolean) => {
    setIsBundle(checked);
    if (checked && selectedBundle === null) {
      setSelectedBundle(0); // Default to the first bundle option
      setQuantity(bundleOptions[0].count);
    } else if (!checked) {
      setSelectedBundle(null);
      setQuantity(1);
    }
  };

  const handleBundleSelection = (index: number) => {
    setSelectedBundle(index);
    setQuantity(bundleOptions[index].count);
  };

  const handleAddOnToggle = (addOnId: string) => {
    setSelectedAddOns((prev) => {
      if (prev.includes(addOnId)) {
        return prev.filter((id) => id !== addOnId);
      } else {
        return [...prev, addOnId];
      }
    });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async () => {
    if (selectedLicenseTier === "custom") {
      // Validate the form
      if (!formData.name || !formData.email || !formData.requirements) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields.",
          variant: "destructive",
        });
        return;
      }
      
      try {
        // Submit the form data to our API
        const response = await fetch('/api/custom-license-request', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            requirements: formData.requirements,
          }),
        });
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.message || 'Failed to submit license request');
        }
        
        // Show success message
        toast({
          title: "Custom License Request Submitted",
          description: "Our team will contact you shortly to discuss your requirements.",
        });
        
        // Log success and close the modal
        console.log("Custom license request submitted:", result);
        handleClose();
      } catch (error) {
        console.error("Error submitting custom license request:", error);
        toast({
          title: "Submission Error",
          description: error instanceof Error ? error.message : "Failed to submit your request. Please try again.",
          variant: "destructive",
        });
      }
      return;
    }
    
    // Normally would submit to payment processor
    toast({
      title: "License Purchase Initiated",
      description: `Your ${tiers.find(t => t.name === selectedLicenseTier)?.name} purchase is being processed.`,
    });
    
    // In a real implementation, we would submit the payment here
    console.log("License purchase:", {
      tier: selectedLicenseTier,
      quantity,
      isBundle,
      bundleOption: isBundle ? bundleOptions[selectedBundle || 0] : null,
      addOns: selectedAddOns,
      totalPrice,
      trackTitle: trackTitle || "Selected Track",
    });
    
    handleClose();
  };

  const toggleLicenseDetails = (tier: string) => {
    setShowLicenseDetails((prev) => ({
      ...prev,
      [tier]: !prev[tier],
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContentWithoutClose className="sm:max-w-[600px] p-0 bg-white overflow-hidden" aria-describedby="license-selection-description">
        <div className="flex flex-col h-full max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <DialogTitle className="text-xl font-bold">License Selection</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="rounded-full h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto">
            <DialogDescription id="license-selection-description" className="sr-only">
              Choose a license type for audio tracks or request a custom license
            </DialogDescription>
            
            {selectedLicenseTier === "custom" ? (
              <div className="py-4">
                <h3 className="text-lg font-semibold mb-4">Custom License Request</h3>
                <p className="mb-6">
                  For customized licensing solutions tailored to your specific needs, 
                  please provide your contact information and requirements. Our team 
                  will reach out to discuss your project in detail.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <input 
                      id="name"
                      type="text" 
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleFormChange}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <input 
                      id="email"
                      type="email" 
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleFormChange}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Phone Number (Optional)</Label>
                    <input 
                      id="phone"
                      type="tel" 
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                      placeholder="+1 (555) 123-4567"
                      value={formData.phone}
                      onChange={handleFormChange}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="requirements">Project Requirements</Label>
                    <textarea 
                      id="requirements"
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[100px] mt-1"
                      placeholder="Please describe your project and licensing needs..."
                      value={formData.requirements}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>
                
                <Button 
                  className="w-full mt-4" 
                  onClick={handleSubmit}
                >
                  Submit Request
                </Button>
              </div>
            ) : (
              <>
                {/* License Type Selection */}
                <div className="mb-6">
                  <h3 className="text-md font-semibold mb-3">Choose License Type</h3>
                  <RadioGroup 
                    defaultValue={selectedLicenseTier}
                    onValueChange={setSelectedLicenseTier}
                    className="grid grid-cols-1 md:grid-cols-3 gap-4"
                  >
                    {tiers.map((tier) => (
                      <div 
                        key={tier.name}
                        className={`border rounded-lg p-4 relative cursor-pointer hover:border-primary transition-colors ${
                          selectedLicenseTier === tier.name ? "border-primary bg-primary/5" : "border-gray-200"
                        }`}
                      >
                        <RadioGroupItem 
                          value={tier.name} 
                          id={`license-${tier.name}`} 
                          className="absolute right-4 top-4" 
                        />
                        <Label
                          htmlFor={`license-${tier.name}`}
                          className="flex flex-col cursor-pointer"
                        >
                          <span className="font-medium">{tier.name}</span>
                          <span className="text-sm text-gray-500">${tier.basePrice}/track</span>
                          
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="mt-2 h-6 px-0 flex items-center text-primary"
                            onClick={(e) => {
                              e.preventDefault();
                              toggleLicenseDetails(tier.name);
                            }}
                          >
                            <span className="text-xs">View details</span>
                            {showLicenseDetails[tier.name] ? (
                              <ChevronUp className="h-3 w-3 ml-1" />
                            ) : (
                              <ChevronDown className="h-3 w-3 ml-1" />
                            )}
                          </Button>

                          {showLicenseDetails[tier.name] && (
                            <ul className="mt-2 space-y-1 text-xs text-gray-600 pl-1">
                              {tier.features.map((feature, i) => (
                                <li key={i} className="flex items-start">
                                  <Check className="h-3 w-3 text-green-500 mr-1.5 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Quantity Selection */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-md font-semibold">Track Quantity</h3>
                    <div className="flex items-center">
                      <span className="text-sm mr-2">Bundle?</span>
                      <Switch
                        checked={isBundle}
                        onCheckedChange={handleBundleToggle}
                      />
                    </div>
                  </div>

                  {isBundle ? (
                    <div className="space-y-3">
                      {bundleOptions.map((bundle, index) => (
                        <div
                          key={index}
                          className={`border rounded-lg p-4 cursor-pointer relative ${
                            selectedBundle === index ? "border-primary bg-primary/5" : "border-gray-200"
                          }`}
                          onClick={() => handleBundleSelection(index)}
                        >
                          <RadioGroupItem 
                            value={`bundle-${index}`}
                            id={`bundle-${index}`}
                            className="absolute right-4 top-4"
                            checked={selectedBundle === index}
                          />
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="font-medium">{bundle.count}-Track Bundle</h4>
                              <div className="text-sm text-gray-600">
                                Save ${selectedLicenseTier === "basic" 
                                  ? bundle.basicSavings 
                                  : selectedLicenseTier === "professional" 
                                    ? bundle.professionalSavings 
                                    : bundle.enterpriseSavings}
                              </div>
                            </div>
                            <div className="text-lg font-bold">
                              ${selectedLicenseTier === "basic" 
                                ? bundle.basicPrice 
                                : selectedLicenseTier === "professional" 
                                  ? bundle.professionalPrice 
                                  : bundle.enterprisePrice}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Select 
                      defaultValue="1" 
                      value={quantity.toString()}
                      onValueChange={handleQuantityChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select quantity" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4].map((num) => (
                          <SelectItem key={num} value={num.toString()}>
                            {num} {num === 1 ? "track" : "tracks"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Add-ons */}
                <div className="mb-6">
                  <h3 className="text-md font-semibold mb-3">Add-ons (Optional)</h3>
                  <div className="space-y-3">
                    {addOns
                      .filter((addOn) => addOn.compatibleTiers.includes(selectedLicenseTier))
                      .map((addOn) => (
                        <div 
                          key={addOn.id} 
                          className={`border rounded-lg p-4 relative ${
                            selectedAddOns.includes(addOn.id) ? "border-primary bg-primary/5" : "border-gray-200"
                          }`}
                        >
                          <div className="flex items-start">
                            <Checkbox
                              id={`addon-${addOn.id}`}
                              checked={selectedAddOns.includes(addOn.id)}
                              onCheckedChange={() => handleAddOnToggle(addOn.id)}
                              className="mt-1"
                            />
                            <div className="ml-3">
                              <Label 
                                htmlFor={`addon-${addOn.id}`}
                                className="font-medium cursor-pointer"
                              >
                                {addOn.name}
                                <span className="ml-1 text-sm font-normal">
                                  (+${addOn.id === "extended-territory" && selectedLicenseTier === "basic" ? 299 : addOn.price})
                                </span>
                              </Label>
                              <p className="text-sm text-gray-600">{addOn.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Price Summary */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Base license ({isBundle ? bundleOptions[selectedBundle || 0].count : quantity} track{(isBundle ? bundleOptions[selectedBundle || 0].count : quantity) > 1 ? "s" : ""})</span>
                    <span>
                      ${isBundle && selectedBundle !== null
                        ? (selectedLicenseTier === "basic"
                          ? bundleOptions[selectedBundle].basicPrice
                          : selectedLicenseTier === "professional"
                            ? bundleOptions[selectedBundle].professionalPrice
                            : bundleOptions[selectedBundle].enterprisePrice)
                        : tiers.find(t => t.name === selectedLicenseTier)?.basePrice * quantity}
                    </span>
                  </div>
                  
                  {selectedAddOns.length > 0 && (
                    <>
                      {selectedAddOns.map((addOnId) => {
                        const addOn = addOns.find((a) => a.id === addOnId);
                        const price = addOnId === "extended-territory" && selectedLicenseTier === "basic" ? 299 : addOn?.price || 0;
                        return (
                          <div key={addOnId} className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-600">{addOn?.name}</span>
                            <span>+${price}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                  
                  <div className="border-t pt-2 mt-2 flex justify-between items-center font-bold">
                    <span>Total</span>
                    <span>${totalPrice}</span>
                  </div>
                </div>

                <Button 
                  className="w-full mt-4" 
                  onClick={handleSubmit}
                >
                  Proceed to Payment
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContentWithoutClose>
    </Dialog>
  );
}; 